import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldArray, Form, FormikProvider, useFormik } from 'formik';
import FormikAutocompletePlaceInput from '@/components/Formik/FormikAutocompletePlaceInput.tsx';
import FormikDatePicker from '@/components/Formik/FormikDatePicker.tsx';
import FormikInput from '@/components/Formik/FormikInput.tsx';
import FormikSelect from '@/components/Formik/FormikSelect.tsx';
import FormikTextArea from '@/components/Formik/FormikTextArea.tsx';
import Button from '@/components/ui/Button/Button.tsx';
import styles from './LogForm.module.scss';
import InfoStatus from '@/components/ui/InfoStatus/InfoStatus.tsx';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import Card from '@/components/ui/Card/Card.tsx';
import { first, last } from 'lodash';
import FormikCheckboxToggle from '@/components/Formik/FormikCheckboxToggle.tsx';
import FormikAutocompleteAirportsInput from '@/components/Formik/FormikAutocompleteAirportsInput.tsx';
import {
	accommodationTypeOptions,
	cabinClassOptions,
	engineTypeOptions,
	groundTransportationOptions,
} from '@/constants/myClimate.ts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys.ts';
import { createTravelLog, deleteTravelLog, getAllItineraries, updateTravelLog } from '@/api/myClimate.ts';
import moment, { Moment } from 'moment';
import { toast } from 'react-toastify';
import handleResponseErrors from '@/utils/handleResponseErrors.ts';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import {
	accommodationValidationSchema,
	airValidationSchema,
	mileageValidationSchema,
} from '@/validations/myClimate.ts';
import CloseIcon from '@/assets/Icons/Close.svg?react';
import GlobalError from '@/components/FormikErrors/GlobalError.tsx';
import classNames from 'classnames';
import breakpoints from '@/constants/breakpoints.ts';
import LeftArrow from '@/assets/Icons/Left-Arrow-Extended.svg?react';
import { trackEvent, trackPostHogEvent } from '@/utils/productAnalyticsUtil.ts';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';
import { productAnalyticsEvents, travelLogAdoptionEvents } from '@/constants/productAnalyticsEvents.ts';
import TravelLogType = App.Enums.TravelLogType;
import TravelLogData = App.Data.TravelLogData;
import CreateTravelLogRequest = App.Data.Request.CreateTravelLogRequest;
import GooglePlaceData = App.Data.GooglePlaceData;
import AirportData = App.Data.AirportData;
import AccommodationType = App.Enums.AccommodationType;
import GroundTransportationType = App.Enums.GroundTransportationType;

export interface DataComplianceLogDefaults {
	accommodationType?: AccommodationType;
	groundTransportationType?: GroundTransportationType;
	arrivalPlace?: GooglePlaceData;
	arrivalPlaceDisplayName?: string;
	flightArrivalAirport?: AirportData;
	calendarStartDate?: Moment;
	calendarEndDate?: Moment;
}

interface LogFormProps {
	tripMode: TravelLogType;
	editingMode: boolean;
	exitEditingMode?: () => void;
	viewMode: boolean;
	selectedRowData?: TravelLogData;
	refetchTravelLogHistoryData?: () => void | Promise<unknown>;
	setSelectedTripMode?: (value: TravelLogType | undefined) => void;
	dataCompliance?: boolean;
	compactDataCompliance?: boolean;
	dataComplianceDefaults?: DataComplianceLogDefaults;
	itineraryId?: number;
	travelerId?: number | null;
	afterDataComplianceSubmitting?: () => void | Promise<void>;
	afterTravelLogSubmitting?: () => void;
	onTravelLogHistoryChanged?: () => void | Promise<void>;
	onSubmittingChange?: (isSubmitting: boolean) => void;
	showSubmittingOverlay?: boolean;
	onContentChange?: () => void;
	onStepBack?: () => void;
}

interface FormTypes extends Omit<CreateTravelLogRequest, 'departureDate' | 'arrivalDate'> {
	departureDate: Moment;
	arrivalDate: Moment;
	reasonDataCompliance: string | undefined;
}

const daysUntilEditModeExpire = (date: string) => {
	const givenTime = moment(date);
	const expireDate = givenTime.add(7, 'days');
	const today = moment();

	return expireDate.diff(today, 'days');
};

const getAirportRouteLabel = (airport?: AirportData) => {
	const airportCodeFromName = airport?.name?.match(/\(([^)]+)\)/)?.[1];
	return airport?.iataCode || airportCodeFromName || '';
};
const getPlaceKey = (place?: GooglePlaceData) =>
	[place?.googlePlaceId, place?.name, place?.addressLong].filter(Boolean).join('|');
const hasFilledValue = (value: unknown) => value !== undefined && value !== null && value !== '';

const LogForm: React.FC<LogFormProps> = ({
	tripMode,
	editingMode,
	exitEditingMode,
	viewMode,
	selectedRowData,
	refetchTravelLogHistoryData,
	setSelectedTripMode,
	dataCompliance,
	compactDataCompliance,
	dataComplianceDefaults,
	itineraryId,
	travelerId,
	afterDataComplianceSubmitting,
	afterTravelLogSubmitting,
	onTravelLogHistoryChanged,
	onSubmittingChange,
	showSubmittingOverlay = true,
	onContentChange,
	onStepBack,
}) => {
	const isAirLog = tripMode === 'flight';
	const isAccommodationLog = tripMode === 'accommodation';
	const isMileageLog = tripMode === 'mileage';
	const mainFormGap = compactDataCompliance ? '18px' : '32px';
	const sectionGap = compactDataCompliance ? '18px' : '32px';
	const fieldRowGap = compactDataCompliance ? '16px' : '24px';
	const fieldColumnGap = compactDataCompliance ? '20px' : '32px';
	const flightLegGap = compactDataCompliance ? '16px' : '25px';

	const queryClient = useQueryClient();
	const invalidateTravelLogAffectedQueries = (includeDataComplianceQueries = true) => {
		const invalidations = [
			queryClient.invalidateQueries({ queryKey: [queryKeys.travelLogHistory] }),
			queryClient.invalidateQueries({ queryKey: [queryKeys.getMyClimateMetrics] }),
			queryClient.invalidateQueries({ queryKey: [queryKeys.getMyClimateCarbonFootprint] }),
			queryClient.invalidateQueries({ queryKey: [queryKeys.metrics] }),
			queryClient.invalidateQueries({ queryKey: [queryKeys.co2Segments] }),
			queryClient.invalidateQueries({ queryKey: [queryKeys.co2Analytics] }),
		];

		if (includeDataComplianceQueries) {
			invalidations.push(
				queryClient.invalidateQueries({ queryKey: [queryKeys.dataComplianceNumOfTrips] }),
				queryClient.invalidateQueries({ queryKey: [queryKeys.dataUncompliantItineraries] }),
				queryClient.invalidateQueries({ queryKey: [queryKeys.dataComplianceSelectableItineraries] }),
				queryClient.invalidateQueries({ queryKey: [queryKeys.dataComplianceSummary] }),
				queryClient.invalidateQueries({ queryKey: [queryKeys.dataCompliancePastTrips] }),
				queryClient.invalidateQueries({ queryKey: [queryKeys.dataComplianceSourceModelItineraries] }),
				queryClient.invalidateQueries({ queryKey: [queryKeys.dataComplianceTravelerItineraries] }),
			);
		}

		return Promise.all(invalidations);
	};
	const currentUserId = useWhoamiStore(state => state.user?.id);
	const [isFormLoading, setIsFormLoading] = useState(false);
	const [isOtherSelected, setIsOtherSelected] = useState(false);
	const shouldDisableTripField =
		viewMode ||
		(editingMode && selectedRowData?.travelerId != null && selectedRowData.travelerId !== currentUserId);
	const setSubmittingLoading = useCallback(
		(isSubmitting: boolean) => {
			setIsFormLoading(isSubmitting);
			onSubmittingChange?.(isSubmitting);
		},
		[onSubmittingChange],
	);
	const { data: allItinerariesOptionsData } = useQuery({
		queryKey: [queryKeys.allItineraries],
		queryFn: getAllItineraries,
	});
	const itineraryOptions = useMemo(() => {
		const options = allItinerariesOptionsData?.itineraries || [];
		const selectedItineraryId = selectedRowData?.itineraryId;

		if (
			selectedItineraryId === undefined ||
			selectedItineraryId === null ||
			options.some(option => String(option.value) === String(selectedItineraryId))
		) {
			return options;
		}

		return [
			...options,
			{
				value: selectedItineraryId,
				label: `Trip #${selectedItineraryId}`,
			},
		];
	}, [allItinerariesOptionsData?.itineraries, selectedRowData?.itineraryId]);

	const commonInitialValues = {
		departureDate: null,
		arrivalDate: null,
		itineraryId: undefined,
		reason: undefined,
		reasonDataCompliance: undefined,
	};

	const typeSpecificInitialValues = {
		flight: {
			isRoundTrip: false,
			flightLegs: [
				{
					departureAirport: {},
					arrivalAirport: dataComplianceDefaults?.flightArrivalAirport || {},
					flightCabinClass: undefined,
				},
			],
		},
		accommodation: {
			accommodationPlace: undefined,
			accommodationType: undefined,
		},
		mileage: {
			departurePlace: undefined,
			arrivalPlace: undefined,
			distance: undefined,
			groundTransportationType: undefined,
		},
	};

	const initialValues = selectedRowData
		? {
				...selectedRowData,
				distance:
					selectedRowData.distance && selectedRowData.distance > 0
						? selectedRowData.distance / 1000
						: selectedRowData.distance,
				departureDate: moment(selectedRowData.departureDate),
				arrivalDate: moment(selectedRowData.arrivalDate),
			}
		: {
				...commonInitialValues,
				...typeSpecificInitialValues[tripMode],
				...(dataCompliance
					? {
							departureDate: dataComplianceDefaults?.calendarStartDate ?? null,
							arrivalDate: dataComplianceDefaults?.calendarEndDate ?? null,
							accommodationType: dataComplianceDefaults?.accommodationType,
							groundTransportationType: dataComplianceDefaults?.groundTransportationType,
							arrivalPlace: dataComplianceDefaults?.arrivalPlace,
						}
					: undefined),
			};

	const validationSchema = {
		flight: airValidationSchema,
		accommodation: accommodationValidationSchema,
		mileage: mileageValidationSchema,
	}[tripMode];

	const transformFormValues = useCallback(
		(values: FormTypes, tripMode: App.Enums.TravelLogType): CreateTravelLogRequest => {
			const defaultIsRoundTrip = {
				flight: false,
				accommodation: null,
				mileage: false,
			}[tripMode];

			const { reasonDataCompliance, distance, ...restValues } = values;

			const formValues = {
				...restValues,
				type: tripMode,
				departureDate: values.departureDate.format('YYYY-MM-DD'),
				arrivalDate: values.arrivalDate.format('YYYY-MM-DD'),
				isRoundTrip: values.isRoundTrip ?? defaultIsRoundTrip ?? undefined,
				...(dataCompliance && {
					reason: reasonDataCompliance === 'Other' ? values.reason : reasonDataCompliance,
				}),
				...(dataCompliance && { itineraryId: itineraryId }),
				...(dataCompliance && travelerId && { travelerId }),
				...(distance && { distance: itineraryId }),
			};

			// Convert distance from kilometers to meters before submitting the request
			if (tripMode === 'mileage') {
				if (typeof values.distance === 'number') {
					formValues.distance = values.distance * 1000;
				} else {
					formValues.distance = undefined;
				}
			}

			return formValues;
		},
		[dataCompliance, itineraryId, travelerId],
	);

	const formik = useFormik({
		initialValues: initialValues as FormTypes,
		enableReinitialize: true,
		validationSchema,
		onSubmit: async values => {
			if (editingMode) {
				refetchTravelLogHistoryData && refetchTravelLogHistoryData();
				return;
			}

			const formValues = transformFormValues(values, tripMode);
			try {
				// Set form loading state to true
				setSubmittingLoading(true);

				// Attempt to create the travel log with form values
				await createTravelLog(formValues);

				if (dataCompliance) {
					trackPostHogEvent('trip_log_created', { category: 'trip-log' });
					trackEvent(productAnalyticsEvents.myClimate.tripLog.actionNeeded.submit, {
						deduplicationKey: `${itineraryId}.${tripMode}`,
						postHogEventName: 'trip_log_action_needed_submitted',
						postHogProperties: {
							category: 'trip-log',
							itinerary_id: itineraryId,
							trip_type: tripMode,
						},
					});
				} else {
					trackEvent(productAnalyticsEvents.myClimate.tripLog.logTrip.submit, {
						deduplicationKey: tripMode,
						postHogEventName: 'trip_log_created',
						postHogProperties: { category: 'trip-log' },
					});
				}

				trackEvent(travelLogAdoptionEvents[tripMode]);

				// Display a success message
				!dataCompliance &&
					!afterTravelLogSubmitting &&
					setTimeout(() => {
						toast.success('You successfully created travel log.');
					}, 300);

				// Keep the reviewed row stable behind the confirmation step. Its owner refreshes
				// compliance data after the modal closes and the row-removal transition completes.
				await invalidateTravelLogAffectedQueries(!dataCompliance);
				await refetchTravelLogHistoryData?.();
				await onTravelLogHistoryChanged?.();

				// Exit editing mode if the function is available
				exitEditingMode && exitEditingMode();

				// Unselect the trip mode
				setSelectedTripMode && setSelectedTripMode(undefined);

				if (dataCompliance) {
					await afterDataComplianceSubmitting?.();
				} else if (afterTravelLogSubmitting) {
					afterTravelLogSubmitting();
				}
			} catch (error) {
				// Handle any errors that occur during the creation process
				handleResponseErrors(error, formik);
				toast.error('We couldn’t save these details. Try again.');
			} finally {
				// Ensure form loading state is set to false after completion
				setSubmittingLoading(false);
			}
		},
	});

	const handleDeleteFormData = async (travelLogId: number) => {
		try {
			// Set form loading state to true
			setSubmittingLoading(true);

			// Attempt to delete the travel log
			await deleteTravelLog(travelLogId);
			trackEvent(productAnalyticsEvents.myClimate.tripLog.history.submit, {
				deduplicationKey: `${travelLogId}.delete`,
				postHogEventName: 'trip_log_deleted',
				postHogProperties: { category: 'trip-log' },
			});

			// Display a success message
			toast.success('You successfully deleted travel log.');

			await invalidateTravelLogAffectedQueries();
			await refetchTravelLogHistoryData?.();
			await onTravelLogHistoryChanged?.();

			// Exit editing mode if the function is available
			exitEditingMode && exitEditingMode();
		} catch (error) {
			// Handle any errors that occur during the delete process
			handleResponseErrors(error, formik);
		} finally {
			// Ensure form loading state is set to false after completion
			setSubmittingLoading(false);
		}
	};

	const handleEditFormData = async () => {
		const formValues = {
			...formik.values,
			departureDate: formik.values.departureDate.format('YYYY-MM-DD'),
			arrivalDate: formik.values.arrivalDate.format('YYYY-MM-DD'),
		};

		// Convert distance from kilometers to meters before submitting the request
		if (formik.values.type === 'mileage') {
			if (typeof formValues.distance === 'number') {
				formValues.distance = formValues.distance * 1000;
			} else {
				formValues.distance = undefined;
			}
		}

		try {
			// Set form loading state to true
			setSubmittingLoading(true);

			// Attempt to update the travel log with form values
			await updateTravelLog(formValues);
			trackEvent(productAnalyticsEvents.myClimate.tripLog.history.submit, {
				deduplicationKey: `${formik.values.id}.edit`,
				postHogEventName: 'trip_log_edited',
				postHogProperties: { category: 'trip-log' },
			});

			// Display a success message
			toast.success('You successfully updated this travel log.');

			await invalidateTravelLogAffectedQueries();
			await refetchTravelLogHistoryData?.();
			await onTravelLogHistoryChanged?.();

			// Exit editing mode if the function is available
			exitEditingMode && exitEditingMode();
		} catch (error) {
			// Handle any errors that occur during the update process
			handleResponseErrors(error, formik);
		} finally {
			// Ensure form loading state is set to false after completion
			setSubmittingLoading(false);
		}
	};

	const previousDistanceRef = useRef<FormTypes['distance']>(formik.values.distance);
	const previousDeparturePlaceKeyRef = useRef(getPlaceKey(formik.values.departurePlace));
	const previousArrivalPlaceKeyRef = useRef(getPlaceKey(formik.values.arrivalPlace));
	const isClearingPlacesForDistanceRef = useRef(false);
	const hasDistanceValue = hasFilledValue(formik.values.distance);
	const shouldPrefillArrivalPlace = dataCompliance && !hasDistanceValue;

	useEffect(() => {
		if (tripMode !== 'mileage') {
			previousDistanceRef.current = formik.values.distance;
			return;
		}

		const distanceChanged = previousDistanceRef.current !== formik.values.distance;
		previousDistanceRef.current = formik.values.distance;

		if (!distanceChanged || !hasDistanceValue) {
			return;
		}

		if (getPlaceKey(formik.values.departurePlace) || getPlaceKey(formik.values.arrivalPlace)) {
			isClearingPlacesForDistanceRef.current = true;
			formik.setFieldValue('departurePlace', {}, false);
			formik.setFieldValue('arrivalPlace', {}, false);
		}
	}, [formik.values.distance, hasDistanceValue, tripMode]);

	useEffect(() => {
		if (tripMode !== 'mileage') {
			previousDeparturePlaceKeyRef.current = getPlaceKey(formik.values.departurePlace);
			previousArrivalPlaceKeyRef.current = getPlaceKey(formik.values.arrivalPlace);
			return;
		}

		const departurePlaceKey = getPlaceKey(formik.values.departurePlace);
		const arrivalPlaceKey = getPlaceKey(formik.values.arrivalPlace);
		const placeChanged =
			previousDeparturePlaceKeyRef.current !== departurePlaceKey ||
			previousArrivalPlaceKeyRef.current !== arrivalPlaceKey;

		previousDeparturePlaceKeyRef.current = departurePlaceKey;
		previousArrivalPlaceKeyRef.current = arrivalPlaceKey;

		if (!placeChanged) {
			return;
		}

		if (isClearingPlacesForDistanceRef.current && !departurePlaceKey && !arrivalPlaceKey) {
			isClearingPlacesForDistanceRef.current = false;
			return;
		}

		if (hasDistanceValue && (departurePlaceKey || arrivalPlaceKey)) {
			formik.setFieldValue('distance', '', false);
		}
	}, [formik.values.departurePlace, formik.values.arrivalPlace, hasDistanceValue, tripMode]);

	useEffect(() => {
		const isDistanceAllowed =
			formik.values.groundTransportationType === 'car' || formik.values.groundTransportationType === 'taxi';

		if (tripMode === 'mileage' && !isDistanceAllowed && formik.values.distance) {
			formik.setFieldValue('distance', '', false);
		}
	}, [formik.values.groundTransportationType]);

	useEffect(() => {
		setIsOtherSelected(formik.values.reasonDataCompliance === 'Other');
		onContentChange && onContentChange();
	}, [formik.values.reasonDataCompliance]);

	const lastArrivalAirport = useMemo(
		() => last(formik.values.flightLegs)?.arrivalAirport,
		[formik.values.flightLegs],
	);

	const isSameDepartureAndArrival = useMemo(() => {
		const firstDepartureAirportId = first(formik.values.flightLegs)?.departureAirport?.id;
		const lastArrivalAirportId = last(formik.values.flightLegs)?.arrivalAirport?.id;

		return !!firstDepartureAirportId && firstDepartureAirportId === lastArrivalAirportId;
	}, [formik.values.flightLegs]);
	const flightRoutePreviewPoints = useMemo(() => {
		const flightLegs = formik.values.flightLegs || [];
		const routePoints = flightLegs.reduce<
			{
				label: string;
				isPlaceholder: boolean;
			}[]
		>((points, flightLeg, index) => {
			const departureLabel = getAirportRouteLabel(flightLeg.departureAirport);
			const arrivalLabel = getAirportRouteLabel(flightLeg.arrivalAirport);
			const isLastLeg = index === flightLegs.length - 1;

			if (index === 0) {
				points.push({ label: departureLabel || 'From', isPlaceholder: !departureLabel });
			} else if (departureLabel && points[points.length - 1]?.label !== departureLabel) {
				points.push({ label: departureLabel, isPlaceholder: false });
			}

			if (arrivalLabel) {
				points.push({ label: arrivalLabel, isPlaceholder: false });
			} else if (isLastLeg) {
				points.push({ label: 'To', isPlaceholder: true });
			}

			return points;
		}, []);
		const firstDepartureLabel = getAirportRouteLabel(first(flightLegs)?.departureAirport);
		const lastRoutePoint = last(routePoints);

		if (
			formik.values.isRoundTrip &&
			firstDepartureLabel &&
			lastRoutePoint?.label !== firstDepartureLabel &&
			!lastRoutePoint?.isPlaceholder
		) {
			routePoints.push({ label: firstDepartureLabel, isPlaceholder: false });
		}

		return routePoints.length
			? routePoints
			: [
					{ label: 'From', isPlaceholder: true },
					{ label: 'To', isPlaceholder: true },
				];
	}, [formik.values.flightLegs, formik.values.isRoundTrip]);
	const isDistanceMileageLog =
		isMileageLog &&
		(formik.values.groundTransportationType === 'car' || formik.values.groundTransportationType === 'taxi');
	const daysLeftToEdit = formik.values.createdAt ? daysUntilEditModeExpire(formik.values.createdAt) : 0;
	const infoStatusText = dataCompliance
		? 'These details can be edited or deleted for one week after they’re added.'
		: viewMode
			? daysLeftToEdit > 0
				? 'If you want to the edit travel log, go back and enter edit mode.'
				: 'Editing is no longer available for this entry. Trip details can only be edited within 1 week of creation.'
			: editingMode
				? daysLeftToEdit === 0
					? 'Note: You can edit or delete this entry only today.'
					: `Note: Only ${daysLeftToEdit} ${daysLeftToEdit === 1 ? 'day' : 'days'} left to edit or delete this entry.`
				: 'Trip details can be edited or deleted within a week of creation.';
	const infoStatusStatus = (editingMode || viewMode) && !dataCompliance ? 'warning' : 'info';
	const outsideGoodwingsActivity = isAccommodationLog ? 'accommodation' : isAirLog ? 'flight' : 'transport';

	return (
		<Card
			className={classNames(
				styles.logFormWrapper,
				dataCompliance && styles.dataCompliance,
				compactDataCompliance && styles.compactDataCompliance,
			)}
		>
			{showSubmittingOverlay && (isFormLoading || formik.isSubmitting) && (
				<Spinner absolute overlay className={styles.formSubmittingSpinner} />
			)}
			<FormikProvider value={formik}>
				<Form className={styles.form}>
					<GlobalError />
					<FlexBlock rowGap={mainFormGap} flexDirection="column">
						{dataCompliance && !compactDataCompliance && (
							<FlexBlock flexDirection="column" rowGap="10px" className={styles.step2Header}>
								<button type="button" onClick={onStepBack}>
									<LeftArrow />
									Back
								</button>
								<p>Trip details</p>
							</FlexBlock>
						)}
						{isAirLog && (
							<FlexBlock
								flexDirection={{
									[breakpoints.zero]: 'column',
									[breakpoints.tabletWide]: 'row',
								}}
								columnGap={{ [breakpoints.tabletWide]: fieldColumnGap }}
								rowGap={{ [breakpoints.zero]: fieldRowGap }}
							>
								<FormikDatePicker
									required
									wildcardVisible
									name="departureDate"
									label="Departure date"
									disabled={viewMode}
									calendarDefaultDate={dataComplianceDefaults?.calendarStartDate}
									className={styles.datePicker}
								/>
								<FormikDatePicker
									required
									wildcardVisible
									name="arrivalDate"
									label="Arrival date"
									disabled={viewMode}
									calendarDefaultDate={dataComplianceDefaults?.calendarEndDate}
									className={styles.datePicker}
								/>
								{!isSameDepartureAndArrival && (
									<div className={styles.toggleBoxContainer}>
										<span className={styles.toggleBoxLabel}>Round trip</span>
										<FormikCheckboxToggle
											name="isRoundTrip"
											disabled={viewMode}
											className={compactDataCompliance ? styles.roundTripToggle : undefined}
										/>
									</div>
								)}
							</FlexBlock>
						)}
						{isAccommodationLog && (
							<FlexBlock
								flexDirection={{
									[breakpoints.zero]: 'column',
									[breakpoints.tabletWide]: 'row',
								}}
								columnGap={{ [breakpoints.tabletWide]: fieldColumnGap }}
								rowGap={{ [breakpoints.zero]: fieldRowGap }}
							>
								<FormikDatePicker
									required
									wildcardVisible
									name="departureDate"
									label="Check-in"
									disabled={viewMode}
									calendarDefaultDate={dataComplianceDefaults?.calendarStartDate}
									className={styles.datePicker}
								/>
								<FormikDatePicker
									required
									wildcardVisible
									name="arrivalDate"
									label="Check-out"
									disabled={viewMode}
									calendarDefaultDate={dataComplianceDefaults?.calendarEndDate}
									className={styles.datePicker}
								/>
							</FlexBlock>
						)}
						<FieldArray
							name="flightLegs"
							render={arrayHelpers => (
								<>
									<FlexBlock flexDirection="column" rowGap={sectionGap}>
										{isAirLog && (
											<FlexBlock flexDirection="column" rowGap={sectionGap}>
												{formik.values?.flightLegs?.map((_, index: number) => {
													const isLast =
														formik.values.flightLegs &&
														index === formik.values.flightLegs.length - 1;

													return (
														<FlexBlock
															flexDirection="column"
															rowGap={flightLegGap}
															key={index}
														>
															<FlexBlock
																flexDirection={{
																	[breakpoints.zero]: 'column',
																	[breakpoints.tabletWide]: 'row',
																}}
																alignItems="flex-start"
																columnGap={{ [breakpoints.tabletWide]: fieldColumnGap }}
																rowGap={{ [breakpoints.zero]: fieldRowGap }}
															>
																<FormikAutocompleteAirportsInput
																	wildcardVisible
																	name={`flightLegs.${index}.departureAirport`}
																	label="From"
																	placeholder="Search by city or airport"
																	disabled={viewMode}
																	className={styles.field}
																/>
																<FormikAutocompleteAirportsInput
																	wildcardVisible
																	name={`flightLegs.${index}.arrivalAirport`}
																	label="To"
																	placeholder="Search by city or airport"
																	disabled={viewMode}
																	className={styles.field}
																/>

																<FlexBlock
																	className={styles.field}
																	flexDirection="row"
																	alignItems="flex-end"
																	columnGap="16px"
																>
																	<FormikSelect
																		required
																		wildcardVisible
																		name={`flightLegs.${index}.flightCabinClass`}
																		options={cabinClassOptions}
																		label="Cabin class"
																		disabled={viewMode}
																		className={styles.cabinClassField}
																	/>
																	{!viewMode &&
																		formik.values.flightLegs &&
																		formik.values.flightLegs.length > 1 && (
																			<button
																				onClick={() =>
																					arrayHelpers.remove(index)
																				}
																				className={styles.deleteFlightRow}
																			>
																				<CloseIcon />
																			</button>
																		)}
																</FlexBlock>
															</FlexBlock>
															{!viewMode && isLast && (
																<FlexBlock
																	alignItems="center"
																	justifyContent="space-between"
																	columnGap="16px"
																	rowGap="8px"
																	className={styles.addDestinationRow}
																>
																	<button
																		type="button"
																		onClick={() =>
																			arrayHelpers.push({
																				departureAirport:
																					lastArrivalAirport || {},
																				arrivalAirport: {},
																			})
																		}
																		className={styles.addFlightRow}
																	>
																		<span>+</span>
																		Add a destination
																	</button>
																	<div className={styles.routePreview}>
																		<span className={styles.routePreviewLabel}>
																			Route
																		</span>
																		<div className={styles.routePreviewPath}>
																			{flightRoutePreviewPoints.map(
																				(routePoint, routePointIndex) => (
																					<React.Fragment
																						key={`${routePoint.label}-${routePointIndex}`}
																					>
																						<strong
																							className={classNames({
																								[styles.routePreviewPlaceholder]:
																									routePoint.isPlaceholder,
																							})}
																						>
																							{routePoint.label}
																						</strong>
																						{routePointIndex <
																							flightRoutePreviewPoints.length -
																								1 && (
																							<span
																								className={
																									styles.routeArrow
																								}
																							>
																								-&gt;
																							</span>
																						)}
																					</React.Fragment>
																				),
																			)}
																		</div>
																	</div>
																</FlexBlock>
															)}
														</FlexBlock>
													);
												})}
											</FlexBlock>
										)}

										{isMileageLog && (
											<FlexBlock flexDirection="column" rowGap={sectionGap}>
												<FlexBlock
													flexDirection={{
														[breakpoints.zero]: 'column',
														[breakpoints.tabletWide]: 'row',
													}}
													columnGap={{ [breakpoints.tabletWide]: fieldColumnGap }}
													rowGap={{ [breakpoints.zero]: fieldRowGap }}
												>
													<FormikDatePicker
														required
														wildcardVisible
														name="departureDate"
														label="Departure date"
														disabled={viewMode}
														calendarDefaultDate={dataComplianceDefaults?.calendarStartDate}
														className={styles.datePicker}
													/>
													<FormikDatePicker
														required
														wildcardVisible
														name="arrivalDate"
														label="Return date"
														disabled={viewMode}
														calendarDefaultDate={dataComplianceDefaults?.calendarEndDate}
														className={styles.datePicker}
													/>
													<div className={styles.toggleBoxContainer}>
														<span className={styles.toggleBoxLabel}>Round trip</span>
														<FormikCheckboxToggle
															name="isRoundTrip"
															disabled={viewMode}
															className={
																compactDataCompliance
																	? styles.roundTripToggle
																	: undefined
															}
														/>
													</div>
												</FlexBlock>
												<FlexBlock
													flexDirection={{
														[breakpoints.zero]: 'column',
														[breakpoints.tabletWide]: 'row',
													}}
													columnGap={{ [breakpoints.tabletWide]: fieldColumnGap }}
													rowGap={{ [breakpoints.zero]: fieldRowGap }}
												>
													<FormikSelect
														required
														wildcardVisible
														name="groundTransportationType"
														options={groundTransportationOptions}
														placeholder="Select category"
														label="Transportation"
														disabled={viewMode}
														className={styles.transportationField}
													/>
													{!dataCompliance && (
														<FormikSelect
															searchable
															name="itineraryId"
															options={itineraryOptions}
															label="Trip"
															disabled={shouldDisableTripField}
															className={styles.tripSelectField}
															compactOptions
														/>
													)}
												</FlexBlock>

												<FlexBlock
													flexDirection={{
														[breakpoints.zero]: 'column',
														[breakpoints.tabletWide]: 'row',
													}}
													columnGap={{ [breakpoints.tabletWide]: fieldColumnGap }}
													rowGap={{ [breakpoints.zero]: fieldRowGap }}
												>
													<FormikAutocompletePlaceInput
														name="departurePlace"
														label="From"
														placeholder="Search city, place or address"
														disabled={viewMode}
														types={
															formik.values.groundTransportationType === 'rail'
																? ['rail_station']
																: undefined
														}
														className={styles.city}
													/>
													<FormikAutocompletePlaceInput
														name="arrivalPlace"
														label="To"
														placeholder="Search city, place or address"
														disabled={viewMode}
														prefillValue={
															shouldPrefillArrivalPlace
																? dataComplianceDefaults?.arrivalPlace
																: undefined
														}
														displayValue={
															shouldPrefillArrivalPlace
																? dataComplianceDefaults?.arrivalPlaceDisplayName
																: undefined
														}
														types={
															formik.values.groundTransportationType === 'rail'
																? ['rail_station']
																: undefined
														}
														className={styles.city}
													/>
													{isDistanceMileageLog && (
														<>
															<span className={styles.mileageDivider}>or</span>
															<FormikInput
																name="distance"
																label="Distance (km)"
																placeholder="Enter distance"
																disabled={viewMode}
																type="number"
																className={styles.distanceField}
															/>
														</>
													)}
												</FlexBlock>
											</FlexBlock>
										)}

										{isAccommodationLog && (
											<FormikAutocompletePlaceInput
												wildcardVisible
												name="accommodationPlace"
												label="City / Accommodation"
												types={['city', 'lodging']}
												placeholder="Search city or accommodation name"
												disabled={viewMode}
												className={styles.city}
											/>
										)}
									</FlexBlock>
								</>
							)}
						/>
						{/* Render fields for other trip types */}
						<div>
							{isAirLog && !dataCompliance && (
								<FlexBlock flexDirection="column" rowGap={fieldRowGap}>
									<FlexBlock columnGap="32px">
										<FormikSelect
											searchable
											name="itineraryId"
											options={itineraryOptions}
											label="Trip"
											disabled={shouldDisableTripField}
											className={styles.tripSelectField}
											compactOptions
										/>
									</FlexBlock>
								</FlexBlock>
							)}
							{isAccommodationLog && (
								<FlexBlock flexDirection="column" rowGap={sectionGap}>
									<FlexBlock
										flexDirection={{
											[breakpoints.zero]: 'column',
											[breakpoints.tabletWide]: 'row',
										}}
										columnGap={{ [breakpoints.tabletWide]: fieldColumnGap }}
										rowGap={{ [breakpoints.zero]: fieldRowGap }}
									>
										{!dataCompliance && (
											<FormikSelect
												searchable
												name="itineraryId"
												options={itineraryOptions}
												label="Trip"
												disabled={shouldDisableTripField}
												className={styles.tripSelectField}
												compactOptions
											/>
										)}
										<FormikSelect
											required
											wildcardVisible
											name="accommodationType"
											options={accommodationTypeOptions}
											label="Category"
											disabled={viewMode}
											className={styles.categoryField}
										/>
									</FlexBlock>
								</FlexBlock>
							)}
							{isMileageLog && isDistanceMileageLog && (
								<FlexBlock flexDirection="column" rowGap={sectionGap}>
									<FormikSelect
										name="engineType"
										options={engineTypeOptions}
										label="Engine type"
										disabled={viewMode}
										className={styles.field}
									/>
								</FlexBlock>
							)}
							{dataCompliance ? (
								<>
									<div className={styles.reasonWrapper}>
										<FormikSelect
											name="reasonDataCompliance"
											label={`Why was this ${outsideGoodwingsActivity} booked outside Goodwings?`}
											options={[
												{
													value: 'Found better price elsewhere',
													label: 'Found a better price elsewhere',
												},
												{
													value: 'Easier to modify/cancel trip',
													label: 'Easier to modify or cancel',
												},
												{
													value: 'Earn personal loyalty points',
													label: 'Wanted to earn personal loyalty points',
												},
												{
													value: 'Unaware of booking requirement',
													label: 'Didn’t know about the booking requirement',
												},
												{
													value: 'Last-minute/emergency travel',
													label: 'Last-minute or emergency travel',
												},
												{
													value: 'Platform too slow/difficult',
													label: 'Booking platform was too slow or difficult to use',
												},
												{
													value: 'Event organizer arranged',
													label: 'Arranged by an event organizer',
												},
												{ value: 'Other', label: 'Other' },
											]}
											className={styles.reason}
											compactOptions
										/>
										<p className={styles.reasonSubtitle}>
											Your answer helps your travel manager understand bookings made outside
											Goodwings.
										</p>
									</div>
									{isOtherSelected && (
										<FormikTextArea
											name="reason"
											label="Tell us more"
											placeholder="Share why this booking was made outside Goodwings"
											disabled={viewMode}
											className={styles.textArea}
										/>
									)}
								</>
							) : (
								<FormikTextArea
									name="reason"
									label="Tell us more"
									placeholder="Share why this booking was made outside Goodwings"
									disabled={viewMode}
									className={styles.textArea}
								/>
							)}
							<div className={styles.formFooter}>
								<InfoStatus
									status={infoStatusStatus}
									text={infoStatusText}
									className={styles.infoStatus}
								/>
								{!viewMode && (
									<FlexBlock columnGap="20px" className={styles.formButtonWrapper}>
										{editingMode ? (
											<>
												{!dataCompliance && (
													<Button
														size="large"
														hover={false}
														onClick={() => handleDeleteFormData(formik.values.id!)}
													>
														Delete
													</Button>
												)}
												{dataCompliance ? (
													<Button color="darkGreen" size="large" type="submit">
														Save details
													</Button>
												) : (
													<Button color="darkGreen" size="large" onClick={handleEditFormData}>
														Save
													</Button>
												)}
											</>
										) : (
											<>
												{compactDataCompliance && onStepBack && (
													<Button hover={false} onClick={onStepBack} size="large">
														Back
													</Button>
												)}
												<Button type="submit" color="darkGreen" size="large">
													Save details
												</Button>
											</>
										)}
									</FlexBlock>
								)}
							</div>
						</div>
					</FlexBlock>
				</Form>
			</FormikProvider>
		</Card>
	);
};

export default LogForm;
